/**
 * Admin Tenants Hooks
 *
 * Custom hooks for admin tenant management.
 *
 * @fileoverview React hooks for admin tenant operations
 */

import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/adminApi';
import {
  AdminTenant,
  TenantQueryParams,
  PaginatedResponse,
  TenantStats,
  CreateTenantInput,
  UpdateTenantInput,
} from '../types/admin';
import { toUserMessage } from '../lib/apiError';

/**
 * Hook for managing tenant list with pagination, search, and filtering
 */
export function useAdminTenants(initialParams: TenantQueryParams = {}) {
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: initialParams.page || 1,
    limit: initialParams.limit || 10,
    total: 0,
    totalPages: 0,
  });
  const [params, setParams] = useState<TenantQueryParams>({
    ...initialParams,
    sortBy: initialParams.sortBy || 'createdAt',
    sortOrder: initialParams.sortOrder || 'desc',
  });

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: PaginatedResponse<AdminTenant> = await adminApi.tenants.list(params);
      setTenants(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load tenants. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const setSearch = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const setIsActive = useCallback((isActive: boolean | undefined) => {
    setParams((prev) => ({ ...prev, isActive, page: 1 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  const setSort = useCallback(
    (sortBy: TenantQueryParams['sortBy'], sortOrder: TenantQueryParams['sortOrder']) => {
      setParams((prev) => ({ ...prev, sortBy, sortOrder }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setParams({
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  }, []);

  const refetch = useCallback(() => {
    fetchTenants();
  }, [fetchTenants]);

  return {
    tenants,
    loading,
    error,
    pagination,
    filters: params,
    setSearch,
    setIsActive,
    setPage,
    setLimit,
    setSort,
    resetFilters,
    refetch,
  };
}

/**
 * Hook for loading all tenants (across all pages)
 */
export function useAllAdminTenants(
  params: Omit<TenantQueryParams, 'page' | 'limit'> = {}
) {
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAllTenants = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const limit = 100;
      let page = 1;
      let totalPages = 1;
      const allTenants: AdminTenant[] = [];

      do {
        const response = await adminApi.tenants.list({
          ...params,
          page,
          limit,
        });

        allTenants.push(...response.data);
        totalPages = response.totalPages;
        page += 1;
      } while (page <= totalPages);

      setTenants(allTenants);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load tenants. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchAllTenants();
  }, [fetchAllTenants]);

  const refetch = useCallback(() => {
    fetchAllTenants();
  }, [fetchAllTenants]);

  return {
    tenants,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for managing a single tenant
 */
export function useAdminTenant(id: string | undefined) {
  const [tenant, setTenant] = useState<AdminTenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTenant = useCallback(async () => {
    if (!id) {
      setTenant(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.tenants.getById(id);
      setTenant(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the tenant. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  const updateTenant = useCallback(
    async (data: UpdateTenantInput) => {
      if (!id) return null;

      setLoading(true);
      setError(null);

      try {
        const updated = await adminApi.tenants.update(id, data);
        setTenant(updated);
        return updated;
      } catch (err) {
        setError(toUserMessage(err, "We couldn't update the tenant. Please try again."));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  const deleteTenant = useCallback(async () => {
    if (!id) return false;

    setLoading(true);
    setError(null);

    try {
      await adminApi.tenants.delete(id);
      setTenant(null);
      return true;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't delete the tenant. Please try again."));
      return false;
    } finally {
      setLoading(false);
    }
  }, [id]);

  const activateTenant = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const updated = await adminApi.tenants.activate(id);
      setTenant(updated);
      return updated;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't activate the tenant. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  const deactivateTenant = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const updated = await adminApi.tenants.deactivate(id);
      setTenant(updated);
      return updated;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't deactivate the tenant. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  return {
    tenant,
    loading,
    error,
    refetch: fetchTenant,
    updateTenant,
    deleteTenant,
    activateTenant,
    deactivateTenant,
  };
}

/**
 * Hook for dashboard statistics
 */
export function useAdminTenantStats() {
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.tenants.getStats();
      setStats(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the statistics. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

/**
 * Hook for creating a new tenant
 */
export function useCreateTenant() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTenant = useCallback(async (data: CreateTenantInput) => {
    setLoading(true);
    setError(null);

    try {
      const tenant = await adminApi.tenants.create(data);
      return tenant;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't create the tenant. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createTenant,
    loading,
    error,
    clearError: () => setError(null),
  };
}

/**
 * Hook for recent tenants (dashboard)
 */
export function useRecentTenants(limit: number = 5) {
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.tenants.getRecent(limit);
      setTenants(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load recent tenants. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  return {
    tenants,
    loading,
    error,
    refetch: fetchRecent,
  };
}
